package hk.com.vr360_demo2.webapp.component.editor_rewrite.component

import emotion.react.css
import kotlinx.browser.window
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.useEffect
import react.useMemo
import react.useState
import web.cssom.Display
import web.cssom.Overflow
import web.cssom.Position
import web.cssom.px

private val hotspotIconNames = (1..17).map { "icon-$it" }.toSet()

fun resolveHotspotIconName(raw: String?): String {
    val value = raw?.trim().orEmpty()
    if (value in hotspotIconNames) return value
    val match = Regex("""icon-(\d+)""").find(value) ?: return "icon-1"
    val index = match.groupValues.getOrNull(1)?.toIntOrNull() ?: return "icon-1"
    return if (index in 1..17) "icon-$index" else "icon-1"
}

fun hotspotIconNameToUrl(iconName: String): String {
    val normalized = resolveHotspotIconName(iconName)
    return "/assets/images/hotspot/${normalized}.png"
}

fun hotspotIconFrameCount(iconName: String): Int {
    val index = Regex("""icon-(\d+)""").find(resolveHotspotIconName(iconName))
        ?.groupValues
        ?.getOrNull(1)
        ?.toIntOrNull()
        ?: 1
    return if (index in 1..7) 25 else 24
}

external interface HotspotAnimatedImageProps : Props {
    var iconName: String
    var alt: String
    var width: Int
    var height: Int
    var backgroundColor: String?
    var fps: Int?
    var startFrameIndex: Int?
}

private const val DEFAULT_FPS = 12

val HotspotAnimatedImage = FC<HotspotAnimatedImageProps> { props ->
    val normalizedIconName = resolveHotspotIconName(props.iconName)
    val spriteUrl = hotspotIconNameToUrl(normalizedIconName)
    val safeFrameCount = hotspotIconFrameCount(normalizedIconName).coerceAtLeast(1)
    val safeStartFrame = useMemo(props.startFrameIndex, safeFrameCount) {
        val requested = props.startFrameIndex ?: 0
        ((requested % safeFrameCount) + safeFrameCount) % safeFrameCount
    }
    val safeFps = (props.fps ?: DEFAULT_FPS).coerceAtLeast(1)
    val fallbackUrl = hotspotIconNameToUrl("icon-1")
    val (resolvedSpriteUrl, setResolvedSpriteUrl) = useState(spriteUrl)
    val (currentFrameIndex, setCurrentFrameIndex) = useState(safeStartFrame)

    useEffect(spriteUrl) {
        setResolvedSpriteUrl(spriteUrl)
    }

    useEffect(dependencies = arrayOf(spriteUrl, safeStartFrame, safeFrameCount)) {
        setCurrentFrameIndex(safeStartFrame)
    }

    useEffect(dependencies = arrayOf(currentFrameIndex, safeFps, safeFrameCount, resolvedSpriteUrl)) {
        if (safeFrameCount <= 1) {
            return@useEffect
        }
        val intervalMs = (1000 / safeFps).coerceAtLeast(16)
        window.setTimeout({
            setCurrentFrameIndex((currentFrameIndex + 1) % safeFrameCount)
        }, intervalMs)
    }

    div {
        css {
            width = props.width.px
            height = props.height.px
            overflow = Overflow.hidden
            position = Position.relative
            display = Display.block
            asDynamic().backgroundColor = props.backgroundColor ?: "transparent"
        }

        img {
            src = resolvedSpriteUrl
            alt = props.alt
            draggable = false
            css {
                width = props.width.px
                height = (props.height * safeFrameCount).px
                display = Display.block
                asDynamic().transform = "translateY(-${currentFrameIndex * props.height}px)"
                asDynamic().transition = "none"
                asDynamic().userSelect = "none"
                asDynamic().webkitUserDrag = "none"
                asDynamic().pointerEvents = "none"
            }
            onDragStart = { it.preventDefault() }
            onError = {
                val target = it.target?.asDynamic()
                if (target != null && fallbackUrl.isNotEmpty() && target.src != fallbackUrl) {
                    setResolvedSpriteUrl(fallbackUrl)
                }
            }
        }

    }
}
